import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { Edit2, Facebook, Globe, Linkedin, Plus, Trash2, Twitter, User } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { Label } from "@/clientWeb/components/ui/label"
import { Textarea } from "@/clientWeb/components/ui/textarea"
import assetSpeaker from '@/assets/speaker.svg'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/clientWeb/components/ui/dialog"
import type { SpeakerData, StepProps } from "./types"

const emptySpeaker = (): Omit<SpeakerData, "id"> => ({
  speakerName: "",
  speakerEmail: "",
  title: "",
  organization: "",
  description: "",
  speakerImage: "",
  linkedinUrl: "",
  facebookUrl: "",
  twitterUrl: "",
  websiteUrl: "",
})

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const URL_RE = /^https?:\/\/.+/

function validateSpeakerForm(form: Omit<SpeakerData, "id">, t: (key: string) => string) {
  const errs: Record<string, string> = {}
  if (!form.speakerName.trim()) {
    errs.speakerName = t('webinar.validation.speakerNameRequired')
  } else if (form.speakerName.trim().length < 2) {
    errs.speakerName = t('webinar.validation.speakerNameMin2')
  } else if (form.speakerName.trim().length > 100) {
    errs.speakerName = t('webinar.validation.speakerNameMax100')
  }
  if (form.speakerEmail.trim() && !EMAIL_RE.test(form.speakerEmail.trim())) {
    errs.speakerEmail = t('webinar.validation.emailInvalid')
  }
  if (form.description.length > 500) {
    errs.description = t('webinar.validation.bioMax500')
  }
  if (form.linkedinUrl.trim() && !URL_RE.test(form.linkedinUrl.trim())) {
    errs.linkedinUrl = t('webinar.validation.urlHttpsRequired')
  }
  if (form.twitterUrl.trim() && !URL_RE.test(form.twitterUrl.trim())) {
    errs.twitterUrl = t('webinar.validation.urlHttpsRequired')
  }
  if (form.facebookUrl.trim() && !URL_RE.test(form.facebookUrl.trim())) {
    errs.facebookUrl = t('webinar.validation.urlHttpsRequired')
  }
  if (form.websiteUrl.trim() && !URL_RE.test(form.websiteUrl.trim())) {
    errs.websiteUrl = t('webinar.validation.urlHttpsRequired')
  }
  return errs
}

export function StepSpeakers({ data, onChange }: StepProps) {
  const t = useTranslation()
  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [form, setForm] = useState(emptySpeaker())
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const openAdd = () => { setForm(emptySpeaker()); setFormErrors({}); setEditId(null); setOpen(true) }
  const openEdit = (speaker: SpeakerData) => { setForm({ ...speaker }); setFormErrors({}); setEditId(speaker.id); setOpen(true) }

  const handleSave = () => {
    const errs = validateSpeakerForm(form, t)
    if (Object.keys(errs).length > 0) { setFormErrors(errs); return }
    if (editId) {
      onChange({ speakers: data.speakers.map((s) => s.id === editId ? { ...form, id: editId } : s) })
    } else {
      onChange({ speakers: [...data.speakers, { ...form, id: `sp-${Date.now()}` }] })
    }
    setOpen(false)
  }

  const handleDelete = (id: string) => {
    onChange({ speakers: data.speakers.filter((s) => s.id !== id) })
    setDeleteId(null)
  }

  const set = (k: keyof typeof form, v: string) => {
    setForm((p) => ({ ...p, [k]: v }))
    setFormErrors((prev) => { const next = { ...prev }; delete next[k]; return next })
  }

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">{t("webinar.speakers")}</p>
          <p className="text-xs text-slate-500 mt-0.5">{t("webinar.addPresentersAndPanelists")}</p>
        </div>
        {data.speakers.length > 0 && (
          <Button type="button" variant="outline" size="sm" onClick={openAdd}>
            <Plus className="w-4 h-4 mr-1" /> {t("webinar.addSpeaker")}
          </Button>
        )}
      </div>

      {/* Empty state */}
      {data.speakers.length === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-stroke-100 dark:border-border dark:border-border flex flex-col items-center justify-center gap-4 py-16 text-center">
          <div className="h-24 w-24 rounded-full flex items-center justify-center">
           <img src={assetSpeaker} alt=""/>
          </div>
          <div>
            <p className="font-semibold text-slate-800 dark:text-white">{t("webinar.noSpeakersYet")}</p>
            <p className="text-sm text-slate-500 mt-1">{t("webinar.addPresentersHere")}</p>
          </div>
          <Button type="button" onClick={openAdd} className="mt-1">
            <Plus className="w-4 h-4 mr-2" /> Add Speaker
          </Button>
        </div>
      )}

      {/* Speakers grid */}
      {data.speakers.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {data.speakers.map((speaker) => (
            <div key={speaker.id}
              className="rounded-xl border border-stroke-100 dark:border-border dark:border-border p-4 flex items-start gap-3 bg-card dark:bg-card">
              {/* Avatar */}
              <div className="shrink-0 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                {speaker.speakerImage
                  ? <img src={speaker.speakerImage} alt={speaker.speakerName} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = "none" }} />
                  : <User className="w-5 h-5 text-primary" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-slate-900 dark:text-foreground/80 dark:text-white truncate">{speaker.speakerName}</p>
                {speaker.title && <p className="text-xs text-slate-500 truncate">{speaker.title}</p>}
                {speaker.organization && <p className="text-xs text-slate-400 truncate">{speaker.organization}</p>}
                <div className="flex gap-1.5 mt-1.5">
                  {speaker.linkedinUrl && <Linkedin className="w-3 h-3 text-blue-500" />}
                  {speaker.twitterUrl && <Twitter className="w-3 h-3 text-sky-400" />}
                  {speaker.facebookUrl && <Facebook className="w-3 h-3 text-blue-600" />}
                  {speaker.websiteUrl && <Globe className="w-3 h-3 text-slate-400" />}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button type="button" onClick={() => openEdit(speaker)}
                  className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-primary transition-colors">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => setDeleteId(speaker.id)}
                  className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-500 hover:text-red-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Speaker Dialog */}
      <Dialog open={open} onOpenChange={setOpen} size="sm">
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? t("webinar.editSpeaker") : t("webinar.addSpeaker")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Image URL */}
            <div>
              <Label className="font-medium mb-1.5 block text-sm">{t("webinar.profileImageUrl")}</Label>
              <div className="flex gap-3 items-center">
                <div className="h-12 w-12 shrink-0 rounded-full bg-slate-100 dark:bg-card overflow-hidden flex items-center justify-center border border-stroke-100 dark:border-border">
                  {form.speakerImage
                    ? <img src={form.speakerImage} alt="preview" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = "none" }} />
                    : <User className="w-5 h-5 text-slate-400" />}
                </div>
                <Input value={form.speakerImage} onChange={(e) => set("speakerImage", e.target.value)}
                  placeholder="https://cdn.example.com/photo.jpg" className="h-10 flex-1" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="font-medium mb-1.5 block text-sm">{t("webinar.name")} <span className="text-red-500">*</span></Label>
                <Input value={form.speakerName} onChange={(e) => set("speakerName", e.target.value)}
                  placeholder="Full name"
                  className={`h-10 ${formErrors.speakerName ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                {formErrors.speakerName && <p className="text-xs text-red-500 mt-1">{formErrors.speakerName}</p>}
              </div>
              <div>
                <Label className="font-medium mb-1.5 block text-sm">{t("webinar.email")}</Label>
                <Input type="email" value={form.speakerEmail} onChange={(e) => set("speakerEmail", e.target.value)}
                  placeholder="speaker@example.com"
                  className={`h-10 ${formErrors.speakerEmail ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                {formErrors.speakerEmail && <p className="text-xs text-red-500 mt-1">{formErrors.speakerEmail}</p>}
              </div>
              <div>
                <Label className="font-medium mb-1.5 block text-sm">{t("webinar.titleRole")}</Label>
                <Input value={form.title} onChange={(e) => set("title", e.target.value)}
                  placeholder="CEO, Researcher, etc." className="h-10" />
              </div>
              <div>
                <Label className="font-medium mb-1.5 block text-sm">{t("webinar.organization")}</Label>
                <Input value={form.organization} onChange={(e) => set("organization", e.target.value)}
                  placeholder="Company or institution" className="h-10" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="font-medium text-sm">{t("webinar.bioDescription")}</Label>
                <span className={`text-xs ${form.description.length > 500 ? "text-red-500" : "text-slate-400"}`}>
                  {form.description.length}/500
                </span>
              </div>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)}
                placeholder="Brief bio shown to attendees" rows={3}
                className={`resize-none ${formErrors.description ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
              {formErrors.description && <p className="text-xs text-red-500 mt-1">{formErrors.description}</p>}
            </div>

            <div className="pt-1">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wide">{t("webinar.socialLinks")}</p>
              <div className="space-y-2">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Linkedin className="w-4 h-4 text-blue-500 shrink-0" />
                    <Input value={form.linkedinUrl} onChange={(e) => set("linkedinUrl", e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                      className={`h-9 ${formErrors.linkedinUrl ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                  </div>
                  {formErrors.linkedinUrl && <p className="text-xs text-red-500 pl-6">{formErrors.linkedinUrl}</p>}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Twitter className="w-4 h-4 text-sky-400 shrink-0" />
                    <Input value={form.twitterUrl} onChange={(e) => set("twitterUrl", e.target.value)}
                      placeholder="https://twitter.com/..."
                      className={`h-9 ${formErrors.twitterUrl ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                  </div>
                  {formErrors.twitterUrl && <p className="text-xs text-red-500 pl-6">{formErrors.twitterUrl}</p>}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Facebook className="w-4 h-4 text-blue-600 shrink-0" />
                    <Input value={form.facebookUrl} onChange={(e) => set("facebookUrl", e.target.value)}
                      placeholder="https://facebook.com/..."
                      className={`h-9 ${formErrors.facebookUrl ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                  </div>
                  {formErrors.facebookUrl && <p className="text-xs text-red-500 pl-6">{formErrors.facebookUrl}</p>}
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-slate-400 shrink-0" />
                    <Input value={form.websiteUrl} onChange={(e) => set("websiteUrl", e.target.value)}
                      placeholder="https://yourwebsite.com"
                      className={`h-9 ${formErrors.websiteUrl ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
                  </div>
                  {formErrors.websiteUrl && <p className="text-xs text-red-500 pl-6">{formErrors.websiteUrl}</p>}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t("webinar.cancel")}</Button>
            <Button type="button" onClick={handleSave}>
              {editId ? t("webinar.saveChanges") : t("webinar.addSpeaker")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)} size="xs">
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("webinar.removeSpeaker")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 dark:text-slate-300 py-1">
            {t("webinar.confirmRemoveSpeaker")}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteId(null)}>{t("webinar.cancel")}</Button>
            <Button type="button" variant="destructive" onClick={() => deleteId && handleDelete(deleteId)}>
              <Trash2 className="w-4 h-4 mr-2" /> {t("webinar.remove")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
